import type { ProjectUserInfo } from "../models/ProjectUserInfo";
import type { ProjektItem } from "../models/ProjektItem";

const RESEARCH_PROJEKTART_IDS = [1, 10];

const hasText = (value: unknown): boolean =>
  typeof value === "string" && value.trim() !== "";

const hasPositiveId = (value: unknown): boolean =>
  typeof value === "number" && value > 0;

const hasItems = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0;

// .NET default DateTime (0001-01-01) is treated as unset
const hasDate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;
  const date = new Date(value as string | Date);
  return !isNaN(date.getTime()) && date.getUTCFullYear() > 1;
};

// Section field keys that store their value under a different ProjektItem key
const FIELD_KEY_TO_REQUIRED_KEY: Record<string, string> = {
  projektauftraggeber: "foerdergeberNeuID",
  nachhaltigkeitszieleCombinedAsText: "nachhaltigkeitsziele",
};

export const isRequiredFieldMissing = (
  missingFields: string[],
  fieldKey: string,
): boolean =>
  missingFields.includes(FIELD_KEY_TO_REQUIRED_KEY[fieldKey] ?? fieldKey);

export const isResearchProjektart = (
  projektartID: number | null | undefined,
): boolean =>
  projektartID !== null &&
  projektartID !== undefined &&
  RESEARCH_PROJEKTART_IDS.includes(projektartID);

/**
 * Missing fields to show in the UI. Only project leaders and privileged users see the markers.
 */
export const getVisibleMissingEckdatenFields = (
  project: ProjektItem | null,
  userInfo?: ProjectUserInfo | null,
): string[] => {
  const canSeeMarkers =
    project?.isBenutzerProjektLeiter === true ||
    userInfo?.isUserInAnyPrivilegedGroup === true;
  return canSeeMarkers ? getMissingEckdatenFields(project) : [];
};

// Required ProjektItem keys grouped by the Project Data tab they are shown on
export const ECKDATEN_TAB_REQUIRED_FIELDS: Record<string, string[]> = {
  allgemein: [
    "title",
    "projekttitelLangform",
    "operativerBeginnPlan",
    "operativesEndePlan",
    "datumEinreichungAngebot",
  ],
  esgfunding: [
    "foerdergeberNeuID",
    "nachhaltigkeitsziele",
    "studiengangZuordnung",
    "auftragsforschung",
    "forschungsschwerpunktInternID",
    "forschungsschwerpunkt2012ID",
  ],
  internaloversight: [],
};

/**
 * Returns the ProjektItem keys that are required but not set.
 * Keys match the ProjektItem property names so sections can highlight them.
 */
export const getMissingEckdatenFields = (
  project: ProjektItem | null,
): string[] => {
  if (!project) return [];

  const missing: string[] = [];
  const require = (key: keyof ProjektItem, isSet: boolean) => {
    if (!isSet) missing.push(key);
  };

  require("title", hasText(project.title));
  require("projekttitelLangform", hasText(project.projekttitelLangform));
  require("operativerBeginnPlan", hasDate(project.operativerBeginnPlan));
  require("operativesEndePlan", hasDate(project.operativesEndePlan));
  require("datumEinreichungAngebot", hasDate(project.datumEinreichungAngebot));
  require("foerdergeberNeuID", hasPositiveId(project.foerdergeberNeuID));
  require("nachhaltigkeitsziele", hasItems(project.nachhaltigkeitsziele));

  if (isResearchProjektart(project.projektartID)) {
    require("studiengangZuordnung", hasItems(project.studiengangZuordnung));
    require("auftragsforschung", project.auftragsforschung !== null &&
      project.auftragsforschung !== undefined);
    require("forschungsschwerpunktInternID", hasPositiveId(
      project.forschungsschwerpunktInternID,
    ));
    require("forschungsschwerpunkt2012ID", hasPositiveId(
      project.forschungsschwerpunkt2012ID,
    ));
  }

  return missing;
};
